import {
  getCountriesSelector,
  getVersionAssetsSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import {
  getClubsSelector,
  getLeaguesSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { bodyTypeIdToName } from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { GridBox } from "../DetailedPlayer";
import { Row } from "./components/Row";

export interface PlayerBioProps {
  displayName: string;
  countryId: number;
  clubId: number;
  leagueId: number;
  possiblePositions: string[];
  cardType: string;
  versionId: number;
  bodyTypeId: number;
  skillMoves: number;
  weakFoot: number;
  preferredFoot: string;
  weight: number;
  height: number;
}

export const PlayerBio = ({
  displayName,
  countryId,
  clubId,
  leagueId,
  possiblePositions,
  cardType,
  versionId,
  bodyTypeId,
  skillMoves,
  weakFoot,
  preferredFoot,
  weight,
  height,
}: PlayerBioProps) => {
  const country = useSelector(getCountriesSelector(countryId))?.name;
  const league = useSelector(getLeaguesSelector(leagueId))?.name;
  const club = useSelector(getClubsSelector(clubId))?.name;
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const versionAssets = useSelector(getVersionAssetsSelector);
  const version = versionAssets[versionId]?.name;
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  return (
    <GridBox
      title="Player Bio"
      className="col-span-4 touch-device:col-span-2 touch-device:min-h-[340px]"
    >
      <div className="flex flex-col mobile:flex-row w-full gap-x-4 mt-4 mobile:mt-0">
        <div className="flex flex-col w-full">
          <Row
            title="Name"
            value={displayName}
            {...(isMobile && { stretch: true })}
          />
          <Row
            title="Country"
            value={
              countryId && (
                <div className="flex flex-row gap-x-1 items-center">
                  <img
                    src={
                      assetsBaseUrl + "/fc25/countries/" + countryId + ".png"
                    }
                    alt={country}
                    className="h-3"
                  />
                  <p>{country}</p>
                </div>
              )
            }
            {...(isMobile && { stretch: true })}
          />
          <Row
            title="Club"
            value={
              clubId && (
                <div className="flex flex-row gap-x-1 items-center">
                  <img
                    src={assetsBaseUrl + "/fc25/clubs/dark/" + clubId + ".png"}
                    alt={club}
                    className="mobile:h-4 h-3"
                  />
                  <p>{club}</p>
                </div>
              )
            }
            {...(isMobile && { stretch: true })}
          />
          <Row
            title="League"
            value={
              leagueId && (
                <div className="flex flex-row gap-x-1">
                  <img
                    src={
                      assetsBaseUrl + "/fc25/leagues/dark/" + leagueId + ".png"
                    }
                    alt={league}
                    className="mobile:h-4 h-3"
                  />
                  <p>{league}</p>
                </div>
              )
            }
            {...(isMobile && { stretch: true })}
          />
          <Row
            title="Positions"
            value={possiblePositions?.join(", ")}
            {...(isMobile && { stretch: true })}
          />
          <Row
            title="Card Type"
            value={cardType}
            {...(isMobile && { stretch: true })}
          />

          <Row
            title="Version"
            value={version}
            hiddenBorder={!isMobile}
            {...(isMobile && { stretch: true })}
          />
        </div>
        <div className="flex flex-col gap-2 w-full">
          <Row
            title="Body Type"
            value={bodyTypeIdToName[bodyTypeId]?.[0]}
            stretch
          />
          <Row title="Skill Moves" value={skillMoves} stretch />
          <Row title="Weak Foot" value={weakFoot} stretch />
          <Row title="Foot" value={preferredFoot} stretch />
          <Row
            title="Height"
            value={
              height && (
                <div className="flex flex-row gap-x-1">
                  <span>{height / 100 + "m"}</span>
                  <span>({cmToFeetAndInches(height)})</span>
                </div>
              )
            }
            stretch
          />
          <Row
            title="Weight"
            value={
              weight && (
                <div className="flex flex-row gap-x-1">
                  <span>{weight + "kg"}</span>
                  <span>({kgToPounds(weight)})</span>
                </div>
              )
            }
            hiddenBorder
          />
        </div>
      </div>
    </GridBox>
  );
};

const cmToFeetAndInches = (cm: number) => {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  // If inches is 12, increment feet and set inches to 0
  if (inches === 12) {
    return `${feet + 1}'0"`;
  }
  return `${feet}'${inches}"`;
};

const kgToPounds = (kg: number) => {
  const pounds = kg * 2.20462; // 1 kg is approximately 2.20462 pounds
  return `${pounds.toFixed(0)}lb`; // rounds to 2 decimal places and appends "lb"
};
